/**
 * Light sync helpers — watermark + outbox order — plan/33, plan/28.
 * Pure (no IndexedDB) so Vitest can cover without jsdom IDB.
 */

export type Watermark = {
  lastUpdated: string | null;
  lastId: string | null;
};

/** Compare (updatedAt, id) for page-cursor watermark — plan/33 */
export function rowAfterWatermark(
  row: { updatedAt: string; id: string },
  since: Watermark,
): boolean {
  if (!since.lastUpdated) return true;
  if (row.updatedAt > since.lastUpdated) return true;
  if (row.updatedAt < since.lastUpdated) return false;
  if (!since.lastId) return true;
  return row.id > since.lastId;
}

/** Paged watermark = last row on this page */
export function watermarkFromPage(
  rows: Array<{ updatedAt: string; id: string }>,
): Watermark {
  const last = rows[rows.length - 1];
  if (!last) return { lastUpdated: null, lastId: null };
  return { lastUpdated: last.updatedAt, lastId: last.id };
}

export type OutboxKind =
  | "pos.drawer.open"
  | "pos.sell"
  | "pos.drawer.close"
  | "pos.void"
  | "pos.return";

/** Local outbox sync plane — plan/28 (server ticket sync_status is separate). */
export type OutboxSyncStatus = "pending" | "conflict";

export type OutboxItem = {
  id: string;
  terminalId: string;
  kind: OutboxKind;
  seq: number;
  payload: unknown;
  createdAt: string;
  syncStatus?: OutboxSyncStatus;
  lastSyncError?: string | null;
  conflictCode?: string | null;
};

/** Soft-limit codes the cashier can confirm to force-sync — plan/14 + plan/28. */
export const SOFT_CONFIRM_CODES = new Set([
  // Legacy outbox may still carry "oversell" — Confirm retries succeed when
  // org allowOversell is on. "oversell_disallowed" is a hard block (not soft).
  "oversell",
  "discount_over_cap",
  "drawer_variance",
  "over_return",
]);

export function isSoftConfirmableCode(code: string | null | undefined): boolean {
  return code != null && SOFT_CONFIRM_CODES.has(code);
}

export function isIdempotentSyncError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes("already") ||
    msg.includes("idempoten") ||
    msg.includes("replay")
  );
}

/** Patch outbox payload so a soft-limit confirm can drain — plan/28. */
export function patchPayloadForSoftConfirm(
  payload: unknown,
  code: string,
): unknown {
  const base =
    payload && typeof payload === "object"
      ? { ...(payload as Record<string, unknown>) }
      : {};
  if (code === "oversell") base.confirmOversell = true;
  if (code === "discount_over_cap") base.confirmDiscountOverCap = true;
  if (code === "drawer_variance") base.confirmVariance = true;
  if (code === "over_return") base.confirmOverReturn = true;
  return base;
}

export function outboxIsConflict(item: OutboxItem): boolean {
  return item.syncStatus === "conflict";
}

export function outboxIsDrainable(item: OutboxItem): boolean {
  return item.syncStatus !== "conflict";
}

/**
 * Causal order per terminal: drawer open → tickets/voids/returns → drawer close.
 * Stable by seq within kind band.
 */
export function sortOutboxForDrain(items: OutboxItem[]): OutboxItem[] {
  const band = (kind: OutboxKind): number => {
    if (kind === "pos.drawer.open") return 0;
    if (kind === "pos.drawer.close") return 2;
    return 1; // sell, void, return
  };
  return [...items].sort((a, b) => {
    if (a.terminalId !== b.terminalId) {
      return a.terminalId.localeCompare(b.terminalId);
    }
    const ba = band(a.kind);
    const bb = band(b.kind);
    if (ba !== bb) return ba - bb;
    return a.seq - b.seq;
  });
}

export type SyncChip = "offline" | "pending" | "conflict" | "synced";

export function syncChipState(input: {
  online: boolean;
  pendingCount: number;
  conflictCount?: number;
}): { chip: SyncChip; label: string } {
  if (!input.online) {
    return { chip: "offline", label: "Offline — cash only" };
  }
  const conflicts = input.conflictCount ?? 0;
  if (conflicts > 0) {
    return {
      chip: "conflict",
      label: `Needs attention (${conflicts})`,
    };
  }
  if (input.pendingCount > 0) {
    return {
      chip: "pending",
      label: `Syncing · ${input.pendingCount} queued`,
    };
  }
  return { chip: "synced", label: "Synced" };
}

/** Soft caps — warn only; cash sales still allowed when cache exists (plan/28). */
export const OUTBOX_SOFT_WARN_COUNT = 25;
export const CATALOG_STALE_HOURS = 12;

export type SoftSyncWarn = {
  kind: "outbox_quota" | "catalog_stale";
  message: string;
};

/** Hours since catalog watermark (null if unknown). */
export function catalogAgeHours(
  catalogUpdatedAt: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!catalogUpdatedAt) return null;
  const t = Date.parse(catalogUpdatedAt);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, (now.getTime() - t) / 3_600_000);
}

/**
 * Soft quota / staleness warns — never block selling.
 * Number-range exhaustion stays a hard block elsewhere.
 */
export function softSyncWarns(input: {
  pendingCount: number;
  catalogUpdatedAt: string | null | undefined;
  now?: Date;
  outboxSoftCap?: number;
  catalogStaleHours?: number;
}): SoftSyncWarn[] {
  const outboxCap = input.outboxSoftCap ?? OUTBOX_SOFT_WARN_COUNT;
  const staleHours = input.catalogStaleHours ?? CATALOG_STALE_HOURS;
  const warns: SoftSyncWarn[] = [];

  if (input.pendingCount >= outboxCap) {
    warns.push({
      kind: "outbox_quota",
      message: `Queue growing (${input.pendingCount}) — sync when you can`,
    });
  }

  const age = catalogAgeHours(input.catalogUpdatedAt, input.now ?? new Date());
  if (age != null && age >= staleHours) {
    const rounded = Math.max(1, Math.floor(age));
    warns.push({
      kind: "catalog_stale",
      message: `Catalog is ${rounded}h old — prices/stock may be stale`,
    });
  }

  return warns;
}

/** Merge delta rows into an existing id-keyed list (last write wins). */
export function mergeById<T extends { id: string }>(
  existing: T[],
  delta: T[],
): T[] {
  const map = new Map(existing.map((r) => [r.id, r]));
  for (const row of delta) map.set(row.id, row);
  return [...map.values()];
}

/** Best-effort network failure (fetch throw / offline). */
export function isLikelyNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network request failed") ||
    msg.includes("load failed")
  );
}

/** Advisory on-hand bump after local/offline sell. */
export function applyLocalStockDelta<
  T extends { id: string; onHand: string },
>(items: T[], lines: Array<{ productId: string; qty: string | number }>): T[] {
  const map = new Map(items.map((i) => [i.id, { ...i }]));
  for (const line of lines) {
    const row = map.get(line.productId);
    if (!row) continue;
    const next = Number(row.onHand) - Number(line.qty);
    row.onHand = String(Number.isFinite(next) ? next : row.onHand);
  }
  return [...map.values()];
}

/** Find a queued sell for a ticket (by outbox id or payload.id). */
export function findPendingSell(
  items: OutboxItem[],
  ticketId: string,
): OutboxItem | null {
  for (const item of items) {
    if (item.kind !== "pos.sell") continue;
    if (item.id === ticketId) return item;
    const payload = item.payload as { id?: string } | null;
    if (payload && typeof payload === "object" && payload.id === ticketId) {
      return item;
    }
  }
  return null;
}

/** True when a return for this ticket is already queued. */
export function findPendingReturn(
  items: OutboxItem[],
  ticketId: string,
): OutboxItem | null {
  for (const item of items) {
    if (item.kind !== "pos.return") continue;
    const payload = item.payload as { sourceId?: string } | null;
    if (payload?.sourceId === ticketId || item.id === `return-${ticketId}`) {
      return item;
    }
  }
  return null;
}

/**
 * Offline return gate — plan/28: synced tickets only.
 * Unsynced same-terminal sales use void + re-ring instead.
 */
export function offlineReturnBlockReason(input: {
  pendingSell: boolean;
  pendingReturn: boolean;
  hasLineCache: boolean;
}): string | null {
  if (input.pendingSell) {
    return "Sale still queued — void & re-ring, or sync first";
  }
  if (input.pendingReturn) {
    return "Return already queued for sync";
  }
  if (!input.hasLineCache) {
    return "Ticket lines not cached — open once online before offline return";
  }
  return null;
}

export type ReturnOutboxPayload = {
  sourceType: "pos_ticket";
  sourceId: string;
  restock: boolean;
  settleMode: "cash_refund";
  idempotencyKey: string;
  warehouseId?: string;
  confirmOverReturn?: boolean;
  lines: Array<{
    sourceLineId: string;
    qty: string;
    lotCode?: string;
    serialCode?: string;
  }>;
};

/** Build a full-qty return payload from a ticket line cache. */
export function buildFullReturnPayload(input: {
  ticketId: string;
  warehouseId?: string | null;
  lines: Array<{
    sourceLineId: string;
    qty: string;
    lotCode?: string | null;
    serialCode?: string | null;
  }>;
  idempotencyKey?: string;
}): ReturnOutboxPayload {
  return {
    sourceType: "pos_ticket",
    sourceId: input.ticketId,
    restock: true,
    settleMode: "cash_refund",
    idempotencyKey: input.idempotencyKey ?? `ret-${input.ticketId}`,
    ...(input.warehouseId ? { warehouseId: input.warehouseId } : {}),
    lines: input.lines
      .filter((l) => Number(l.qty) > 0)
      .map((l) => ({
        sourceLineId: l.sourceLineId,
        qty: String(l.qty),
        ...(l.lotCode ? { lotCode: l.lotCode } : {}),
        ...(l.serialCode ? { serialCode: l.serialCode } : {}),
      })),
  };
}

/** Restock advisory on-hand after local void of an unsynced sell. */
export function reverseLocalStockDelta<
  T extends { id: string; onHand: string },
>(items: T[], lines: Array<{ productId: string; qty: string | number }>): T[] {
  return applyLocalStockDelta(
    items,
    lines.map((l) => ({
      productId: l.productId,
      qty: -Number(l.qty),
    })),
  );
}
